import './AtmosphereSettingsPanel.css';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';
import type { AtmosphereSettings } from '../../stores/atmosphereSettingsStore';
import { DICE_PALETTE_OPTIONS } from '../atmosphere/dicePalettes';
import { DICE_THEME_OPTIONS } from '../atmosphere/diceThemes';

/**
 * A9 (2026-06-04) — Atmosphere settings UI.
 *
 * Exposes all 13 cinematic toggles + the Minimal Mode preset button +
 * a Reset button. When the OS reports `prefers-reduced-motion: reduce`,
 * the animation toggles render disabled with a banner notice (the user
 * cannot defeat the OS override).
 *
 * Mount target: ScenarioRunner's drawer slot at `drawer === 'atmosphere'`.
 */

interface ToggleRowProps {
  label: string;
  description?: string;
  settingKey: keyof Pick<
    AtmosphereSettings,
    | 'gunBarrelOpenerEnabled'
    | 'diceTumbleEnabled'
    | 'voiceTumbleDifferentialEnabled'
    | 'voiceConfirmStingEnabled'
    | 'qrCinematicsEnabled'
    | 'qrBrassSwellEnabled'
    | 'qrFlavorStringsEnabled'
    | 'villainEntranceStingsEnabled'
    | 'moduleSkinsEnabled'
    | 'heroPointChipEnabled'
    | 'qBranchVoiceOverEnabled'
    | 'dossierRedactionEnabled'
    | 'animationsEnabled'
    | 'sfxEnabled'
    | 'fameRecognitionCinematicEnabled'
    | 'manualBadgeVisibleEnabled'
    | 'diceChipFlyEnabled'
    | 'voiceDebugEnabled'
  >;
  /** When true, the row renders disabled with a notice (OS override). */
  forcedOff?: boolean;
  /** Optional testid override; default 'br-atmosphere-toggle-${settingKey}'. */
  testid?: string;
}

function ToggleRow({ label, description, settingKey, forcedOff, testid }: ToggleRowProps) {
  const settings = useAtmosphereSettings();
  // Read the underlying boolean — for animationsEnabled the derived
  // value reflects the OS override but the input should still show the
  // user's stored choice. For now the difference only matters for
  // animationsEnabled; other toggles aren't OS-overridden.
  const value = settings[settingKey] as boolean;
  const id = testid ?? `br-atmosphere-toggle-${settingKey}`;
  return (
    <label className="br-atmosphere-toggle-row" data-testid={`${id}-row`}>
      <input
        type="checkbox"
        data-testid={id}
        checked={value}
        disabled={forcedOff}
        onChange={(e) =>
          settings.setSetting(settingKey, e.target.checked as AtmosphereSettings[typeof settingKey])
        }
      />
      <div className="br-atmosphere-toggle-text">
        <span className="br-atmosphere-toggle-label">{label}</span>
        {description && (
          <span className="br-atmosphere-toggle-description">{description}</span>
        )}
      </div>
    </label>
  );
}

export interface AtmosphereSettingsPanelProps {
  testidPrefix?: string;
}

export function AtmosphereSettingsPanel({
  testidPrefix = 'br-atmosphere-settings',
}: AtmosphereSettingsPanelProps) {
  const settings = useAtmosphereSettings();
  return (
    <aside
      className="br-scenario-drawer br-atmosphere-settings"
      data-testid={`${testidPrefix}-panel`}
      aria-label="Atmosphere settings"
    >
      <h2 className="br-label">Atmosphere Settings</h2>

      {settings.prefersReducedMotion && (
        <div
          className="br-atmosphere-os-notice"
          data-testid={`${testidPrefix}-os-notice`}
          role="note"
        >
          Motion reduced by OS setting — animation toggles disabled.
        </div>
      )}

      <section className="br-atmosphere-section">
        <h3 className="br-label">Master</h3>
        <ToggleRow
          label="Animations"
          description="3D dice tumble, gold-leaf flash, hero-point chip flight"
          settingKey="animationsEnabled"
          forcedOff={settings.prefersReducedMotion}
        />
        <ToggleRow
          label="SFX"
          description="All synthesized cinematic audio (brass, thunk, chime, dice rattle)"
          settingKey="sfxEnabled"
        />
      </section>

      <section className="br-atmosphere-section">
        <h3 className="br-label">Roll Cinematic (A2 / A3)</h3>
        <div
          className="br-atmosphere-slider-row"
          data-testid={`${testidPrefix}-dice-volume-row`}
        >
          <div className="br-atmosphere-slider-header">
            <label
              htmlFor={`${testidPrefix}-dice-volume`}
              className="br-atmosphere-toggle-label"
            >
              Dice rattle volume
            </label>
            <button
              type="button"
              className="br-atmosphere-mute-button"
              data-testid={`${testidPrefix}-dice-mute`}
              data-muted={settings.diceTumbleSfxVolume === 0 ? 'true' : 'false'}
              onClick={() => {
                settings.setSetting(
                  'diceTumbleSfxVolume',
                  settings.diceTumbleSfxVolume === 0 ? 0.3 : 0,
                );
              }}
              aria-label={
                settings.diceTumbleSfxVolume === 0
                  ? 'Unmute dice rattle'
                  : 'Mute dice rattle'
              }
              title={
                settings.diceTumbleSfxVolume === 0
                  ? 'Click to unmute (restores default 0.3)'
                  : 'Click to mute the dice rattle SFX'
              }
            >
              {settings.diceTumbleSfxVolume === 0 ? '🔇' : '🔊'}
            </button>
          </div>
          <input
            id={`${testidPrefix}-dice-volume`}
            data-testid={`${testidPrefix}-dice-volume`}
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={settings.diceTumbleSfxVolume}
            onChange={(e) =>
              settings.setSetting('diceTumbleSfxVolume', Number(e.target.value))
            }
          />
          <span className="br-atmosphere-slider-value">
            {Math.round(settings.diceTumbleSfxVolume * 100)}%
          </span>
        </div>
        <ToggleRow
          label="D100 dice tumble"
          description="3D framer-motion tumble on roll resolution"
          settingKey="diceTumbleEnabled"
          forcedOff={settings.prefersReducedMotion}
        />
        <ToggleRow
          label="Voice vs selector differential"
          description="Voice rolls get the +0.3s tumble + transcript bubble"
          settingKey="voiceTumbleDifferentialEnabled"
        />
        <ToggleRow
          label="Voice confirm sting"
          description='"Confirmed — Excellent." chime on QR 1 + voice'
          settingKey="voiceConfirmStingEnabled"
        />
        <ToggleRow
          label="QR cinematic responses"
          description="Card + gold-leaf flash + brass swell after the tumble"
          settingKey="qrCinematicsEnabled"
          forcedOff={settings.prefersReducedMotion}
        />
        <ToggleRow
          label="QR brass-swell SFX"
          description="3-note brass cluster at QR 1-3; dry thunk at QR 4/Failure"
          settingKey="qrBrassSwellEnabled"
        />
        <ToggleRow
          label="QR flavor strings"
          description="Sampled narrative one-liner per QR level"
          settingKey="qrFlavorStringsEnabled"
        />

        {/* 2026-06-05 cinematic-linger: user-controlled dismiss mode + hold duration. */}
        <div
          className="br-atmosphere-select-row"
          data-testid={`${testidPrefix}-dismiss-mode-row`}
        >
          <div className="br-atmosphere-toggle-text">
            <label
              htmlFor={`${testidPrefix}-dismiss-mode`}
              className="br-atmosphere-toggle-label"
            >
              Cinematic dismiss
            </label>
            <span className="br-atmosphere-toggle-description">
              Auto: fades after the hold timer. Manual: stays until you click — useful for slow read or when narrating.
            </span>
          </div>
          <select
            id={`${testidPrefix}-dismiss-mode`}
            data-testid={`${testidPrefix}-dismiss-mode`}
            value={settings.cinematicDismissMode}
            onChange={(e) =>
              settings.setSetting(
                'cinematicDismissMode',
                e.target.value as 'auto' | 'manual',
              )
            }
          >
            <option value="auto">Auto (timed)</option>
            <option value="manual">Manual (click to dismiss)</option>
          </select>
        </div>
        <div
          className="br-atmosphere-slider-row"
          data-testid={`${testidPrefix}-hold-ms-row`}
        >
          <div className="br-atmosphere-slider-header">
            <label
              htmlFor={`${testidPrefix}-hold-ms`}
              className="br-atmosphere-toggle-label"
            >
              Cinematic hold (auto mode)
            </label>
            <span className="br-atmosphere-toggle-description">
              How long the QR result + Fame reveal stay on screen in auto mode. Min 1500ms / max 10000ms. Per-QR floors still apply.
            </span>
          </div>
          <input
            id={`${testidPrefix}-hold-ms`}
            data-testid={`${testidPrefix}-hold-ms`}
            type="range"
            min={1500}
            max={10000}
            step={500}
            value={settings.cinematicHoldMs}
            onChange={(e) =>
              settings.setSetting('cinematicHoldMs', Number(e.target.value))
            }
            disabled={settings.cinematicDismissMode === 'manual'}
          />
          <span className="br-atmosphere-slider-value">
            {(settings.cinematicHoldMs / 1000).toFixed(1)}s
          </span>
        </div>
        {/* 2026-06-05 round 9 dice-feedback: user-selectable percentile dice palette. */}
        <div
          className="br-atmosphere-select-row"
          data-testid={`${testidPrefix}-dice-palette-row`}
        >
          <div className="br-atmosphere-toggle-text">
            <label
              htmlFor={`${testidPrefix}-dice-palette`}
              className="br-atmosphere-toggle-label"
            >
              Percentile dice palette
            </label>
            <span className="br-atmosphere-toggle-description">
              Two distinct colors for the tens (00–90) and ones (0–9) d10s.
            </span>
          </div>
          <select
            id={`${testidPrefix}-dice-palette`}
            data-testid={`${testidPrefix}-dice-palette`}
            value={settings.dicePalette}
            onChange={(e) =>
              settings.setSetting(
                'dicePalette',
                e.target.value as typeof settings.dicePalette,
              )
            }
          >
            {DICE_PALETTE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>
        {/* Sprint 3 §4.2 (2026-06-06): dice-box substrate theme. Composes
            with palette — theme swaps surface texture, palette tints color. */}
        <div
          className="br-atmosphere-select-row"
          data-testid={`${testidPrefix}-dice-theme-row`}
        >
          <div className="br-atmosphere-toggle-text">
            <label
              htmlFor={`${testidPrefix}-dice-theme`}
              className="br-atmosphere-toggle-label"
            >
              Dice substrate theme
            </label>
            <span className="br-atmosphere-toggle-description">
              Physical-material variant. Composes with the palette: theme tints the substrate, palette tints the face.
            </span>
          </div>
          <select
            id={`${testidPrefix}-dice-theme`}
            data-testid={`${testidPrefix}-dice-theme`}
            value={settings.diceTheme}
            onChange={(e) =>
              settings.setSetting(
                'diceTheme',
                e.target.value as typeof settings.diceTheme,
              )
            }
          >
            {DICE_THEME_OPTIONS.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>
      </section>

      <section className="br-atmosphere-section">
        <h3 className="br-label">Hero Point (A6)</h3>
        <ToggleRow
          label="Hero Point chip animation"
          description="Chip dispatch from PC portrait to roll card on HP spend"
          settingKey="heroPointChipEnabled"
          forcedOff={settings.prefersReducedMotion}
        />
        <ToggleRow
          label="Dice result chip fly (Skill Check only)"
          description="Chip flies from the dice overlay to the History toggle after skill check rolls settle. Save, damage, and percentile rolls skip — their cinematic IS the reveal."
          settingKey="diceChipFlyEnabled"
          forcedOff={settings.prefersReducedMotion}
        />
      </section>

      <section className="br-atmosphere-section">
        <h3 className="br-label">Forthcoming (A4 / A5 / A7 / A8)</h3>
        <ToggleRow
          label="Villain entrance stings"
          description="Sting + portrait reveal when a major villain joins the Combatant Tracker"
          settingKey="villainEntranceStingsEnabled"
        />
        <ToggleRow
          label="Module-themed skins"
          description="Per-module palette + background override"
          settingKey="moduleSkinsEnabled"
        />
        <ToggleRow
          label="Q Branch voice-over"
          description="LLM-generated Q narration on the loadout screen (module-host only)"
          settingKey="qBranchVoiceOverEnabled"
        />
        <ToggleRow
          label="Dossier redaction bars"
          description="Black bars over unrecognized TAROT operatives in the exported mission dossier"
          settingKey="dossierRedactionEnabled"
        />
        <ToggleRow
          label="Fame Recognition cinematic (A8r Slice C)"
          description="Dice tumble + IDENTIFIED/UNKNOWN reveal pane when Bond encounters a TAROT operative for the first time. Slice B's recognition logic still runs when this is off — just no visual flourish."
          settingKey="fameRecognitionCinematicEnabled"
          forcedOff={settings.prefersReducedMotion}
        />
        <ToggleRow
          label="Manual recognition badge (A8r Slice D)"
          description="Small [GM] marker next to NPC names whose recognition is currently GM-overridden (rather than from the natural roll). Pure cosmetic surface — the underlying override state is unaffected."
          settingKey="manualBadgeVisibleEnabled"
        />
      </section>

      {/* Sprint 4 §4.5 (2026-06-06): voice debug surface. Off by default;
          when ON, ScenarioRunner renders a small textarea + Send button
          that calls dispatcher.dispatchTranscript directly — bypasses the
          mic / STT layer. Used by briefing-room-voice-force-dice-roll
          Playwright spec for hybrid (mocked-route) tests. */}
      <section className="br-atmosphere-section">
        <h3 className="br-label">Debug</h3>
        <ToggleRow
          label="Voice transcript debug control"
          description="Surfaces a small textarea + Send button in the scenario runner that injects a transcript through the GM dispatcher (skipping mic / STT). Used for testing voice-driven tool calls (force_dice_roll, force_recognize_npc, etc) without a mic."
          settingKey="voiceDebugEnabled"
        />
      </section>

      <section
        className="br-atmosphere-section br-atmosphere-actions"
        data-testid={`${testidPrefix}-actions`}
      >
        <button
          type="button"
          className="br-atmosphere-button"
          data-testid={`${testidPrefix}-minimal`}
          onClick={settings.applyMinimalPreset}
        >
          Apply Minimal Mode
        </button>
        <button
          type="button"
          className="br-atmosphere-button"
          data-testid={`${testidPrefix}-reset`}
          onClick={settings.resetToDefaults}
        >
          Reset to Defaults
        </button>
      </section>
    </aside>
  );
}

export default AtmosphereSettingsPanel;
